import type { ActionGraph } from "../actions.ts";
import { EVENT_SPECS } from "../schemaSpec.ts";
import type {
	Issue,
	IssueContext,
	IssuePath,
	ValidationReport,
} from "./types.ts";

export const EVENT_TYPE_SET = new Set<string>(EVENT_SPECS.map((e) => e.type));

export const newReport = (): ValidationReport => ({
	errors: [],
	warnings: [],
	brokenRefs: {
		items: {},
		attacks: {},
		recipes: {},
		currencies: {},
		stats: {},
		quests: {},
	},
});

export const pushBrokenRef = (
	report: ValidationReport,
	kind: keyof ValidationReport["brokenRefs"],
	entityId: string,
	fieldPath: string,
): void => {
	const map = report.brokenRefs[kind];
	if (!map[entityId]) map[entityId] = [];
	map[entityId].push(fieldPath);
};

export const formatPath = (path: IssuePath): string =>
	path
		.map((p, i) =>
			typeof p === "number" ? `[${p}]` : i === 0 ? p : `.${p}`,
		)
		.join("");

export const issue = (
	severity: Issue["severity"],
	entityKind: Issue["entityKind"],
	entityId: string | null,
	path: IssuePath,
	message: string,
	code?: string,
	context?: IssueContext,
): Issue => ({ severity, entityKind, entityId, path, message, code, context });

export const graphContainsType = (
	graph: ActionGraph | null | undefined,
	targetType: string,
): boolean => {
	if (!graph) return false;
	for (const node of Object.values(graph.nodes)) {
		if (node.type === targetType) return true;
	}
	return false;
};

export const graphContainsBehavior = (
	graph: ActionGraph | null | undefined,
	behaviorTypes: string[],
): boolean => {
	if (!graph) return false;
	for (const node of Object.values(graph.nodes)) {
		if (node.type !== "ChangeBehavior") continue;
		const bt = (node as unknown as Record<string, unknown>).behaviorType as
			| string
			| undefined;
		if (bt && behaviorTypes.includes(bt)) return true;
	}
	return false;
};

export const validateShape = (
	schema: {
		safeParse: (v: unknown) => {
			success: boolean;
			error?: {
				issues: {
					path: (string | number)[];
					message: string;
					code?: string;
				}[];
			};
		};
	},
	value: unknown,
	kind: Issue["entityKind"],
	entityId: string,
	report: ValidationReport,
): void => {
	const res = schema.safeParse(value);
	if (!res.success && res.error) {
		for (const iss of res.error.issues) {
			report.errors.push(
				issue("error", kind, entityId, iss.path, iss.message, iss.code),
			);
		}
	}
};
